import memoize from 'lodash/memoize'
import { autorun, action, makeObservable, observable } from 'mobx'
import { types as t } from '@invisr/mobx-state-tree'
import errorToast from '../../utils/error-toast'
import Mapping from './mapping'
import merge from 'lodash/merge'
import type { WithDS } from "./datasource-type";

export class OpenAPIProvider {
  config = null
  isLoading = false

  setIsLoading = () => {
    this.isLoading = true
  }

  setIsLoadingOff = () => {
    this.isLoading = false
  }

  query = () => {}
  run = async (operationId, parameters, requestBody) => {
    try {
      const customHeaders = await this.config.mapping.mapper.convertProps()
      if (requestBody instanceof FormData) {
        customHeaders['Content-Type'] = 'multipart/form-data'
      }
      const SwaggerClient = (await import('swagger-client')).default 
      const result = await SwaggerClient.execute({
        spec: this.config.spec,
        operationId,
        parameters,
        requestBody,
        requestInterceptor: customHeaders && ((request) => {
          for (const k in customHeaders) request.headers[k] = customHeaders[k]
          return request
        })
      })
      return result.body ?? result.data
    } catch(e) {
      errorToast(`${this.config.name} ${operationId}`, e.message)
    }
  }

  init = () => {
    for(let m of this.config.methods) {
      const k = m?.operationId
      if(!k) continue
      if(this[k]) continue
      Object.defineProperty(this, k, {
        enumerable: true,
        configurable: true,
        value: this.run.bind(this, k)
      })
    }
  }

  constructor (config) {
    makeObservable(this, {
      isLoading: observable,
      setIsLoading: action,
      setIsLoadingOff: action
    })
    
    this.config = config
    this.init()
    autorun(() => this.init())
  }
}

const OpenAPIModel = t
  .model('OpenAPI', {
    type: t.optional(t.string, 'OpenAPI'),
    url: t.optional(t.string, ''),
    mapping: t.maybeNull(t.late(() => Mapping)),
    spec: t.frozen({ paths: {}, openapi: void 0, swagger: void 0, servers: void 0})
  })

export default (OpenAPIModel as WithDS<typeof OpenAPIModel>)
  .views(self => ({
    parseSpec(spec = self.spec, operationIds?, full = false) {
      try {
        const paths = {}
        for(let p in spec.paths || {}) {
          for(let m in spec.paths[p] || {}) {
            const s = spec.paths[p][m] || {}
            if(operationIds) {
              if(!operationIds.has(s.operationId)) continue
            }
            paths[p] = paths[p] || {}
            paths[p][m] = full ? s : {operationId: s.operationId, parameters: s.parameters}
          }
        }
        const { openapi, swagger, servers } = spec
        return { openapi, swagger, servers, paths }
      } catch(e) {
        console.error(e)
      }
    },
    getMethods(paths) {
      const items = []
      for(let p in paths || {}) {
        for(let m in paths[p] || {}) {
          items.push(paths[p][m])
        }
      }
      return items
    },
    get methods(): { operationId: string }[] {
      const paths = (self as any).parseSpec(self.spec).paths
      return (self as any).getMethods(paths) || []
    },
    getFields () {
      return []
    },
    get fieldSet (): Field[] {
      return (self as any).getFields()
    },
    get provider () {
      return OpenAPIProvider
    },
    get fields (): Field[] {
      return (self as any).getFields()
    },
    get isValid () {
      return true
    },
    get fullSpec() {
      if(!(self as any).fullSpecResult?.spec) return
    },
  }))
  .views(self => ({
    fetchParameters: async (method) => {
      let methods = [method]
      const s = self as any
      if(!s.fullSpecPromise) {
        s.setFullSpecPromise(s.loadFullSpec())
      }
      await s.fullSpecPromise
      
      let fullSpec = s.fullSpecResult?.spec

      if(!fullSpec) return []
      const filteredSpec = {...fullSpec, ...self.parseSpec(fullSpec, new Set(methods.map(m => m.operationId)), true)}
      const SwaggerClient = (await import('swagger-client')).default 
      const { spec } = await SwaggerClient.resolve({
        spec: filteredSpec,
        pathDiscriminator: ['paths']
      })
      methods = self.getMethods(spec.paths)
      const m = methods[0]
      
      return m && paramsToMapping(m.parameters, m.requestBody)
    },

    labels () {
      const s = self as WithDS<typeof self>
      const labels = {
        key: s.id,
        label: s.name,
        childNodes: [
          {
            key: 'methods',
            label: 'Methods',
            childNodes: self.methods.map(m => ({
              key: m.operationId,
              label: m.operationId,
              meta: { fieldPath: 'operationId', parameters: (self as any).fetchParameters.bind(self, m) },
              secondaryLabel: 'FUNCTION'
            }))
          },
          {
            key: 'state',
            label: 'State',
            childNodes: [
              {
                key: 'isLoading',
                label: 'isLoading',
                secondaryLabel: 'state'
              }
            ]
          }
        ]
      }
      return labels
    }
  }))
  .volatile(() => ({
    fullSpecResult: null,
    fullSpecPromise: null
  }))
  .actions(self => ({
    setFullSpecPromise(res) {
      self.fullSpecPromise = res
    },    
    setFullSpecResult(res) {
      self.fullSpecResult = res
    }
  }))
  .actions(self => ({
    async loadFullSpec(url = self.url, update = false) {
      const SwaggerClient = (await import('swagger-client')).default       
      const res = await SwaggerClient.resolve({ url, pathDiscriminator: ['asdfasd'] })
      const fullSpec = res.spec
      if(fullSpec && update) {
        const { spec: newSpec } = await SwaggerClient.resolve({
          spec: {...fullSpec, ...self.parseSpec(fullSpec, new Set(self.methods.map(m => m.operationId)), true)},
          pathDiscriminator: ['paths']
        })
	
	const s = self as any
	
        s.update('spec', {
          ...self.parseSpec(newSpec),
          servers: self.spec?.servers || newSpec.servers
        })
      }
      
      self.setFullSpecResult(res)
      return res
    },
    afterCreate () {
      if (!self.mapping) {
	const s = self as any
        s.update('mapping', {
          type: 'object',
          children: []
        })
      }
    }
  }))


function paramsToMapping(params = [], requestBody) {
  let body
  if(requestBody) {
    body = requestBody.content['application/json']
    if(!body && requestBody.content) body = Object.values(requestBody.content)[0]
  }
  const res = []
  if(params.length) {
    res.push({
      key: 'parameters',
      type: 'object',
      bindingType: 'value',
      children: params.map(p => ({
        key: p.name,
        ...parseSchema(p.schema || p)
      }))
    })
  }
  if(body) {
    res.push({
      key: 'requestBody',
      bindingType: 'value',
      ...parseSchema(body.schema || body)
    })
  }
  return res
}

type FieldType = 'number'|'bool'|'object'|'list'|'multiselect'|'string'
interface Field {
  type: FieldType
  value: string
  bindingType: string
  meta?: Record<string, string>
  children?: (Field & { key: string })[]
}
function parseSchema(sch): Field {
  let schema = sch
  if(schema?.allOf) {
    schema = merge({}, ...schema.allOf)
  }
  const obj: Partial<Field> = {
    type: getType(schema)
  }
  
  if(schema.default) {
    obj.value = schema.default
    obj.bindingType = 'value'
  }

  if(obj.type === 'list' && schema.items) {
    if(schema.items.enum) {
      obj.type = 'multiselect'
      obj.meta = { picklist: schema.items.enum }
    } else {
      obj.children = [{ key: '0', ...parseSchema(schema.items)}]
    }
  }
  
  if(obj.type === 'object' && schema.properties) {
    obj.children = obj.children || []    
    for(let key in schema.properties) {
      obj.children.push({ key, ...parseSchema(schema.properties[key])})
    }
  }
  return obj as Field
}

function getType(schema): FieldType {
  switch(schema.type) {
    case 'integer':
    case 'number':
      return 'number'
    case 'boolean':
      return 'bool'
    case 'object':
      return 'object'
    case 'array':
      return 'list'
  }

  return 'string'
}
